// pages/Settings.jsx
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  FiUser, 
  FiLock, 
  FiBell, 
  FiShield,
  FiLogOut,
  FiChevronRight,
  FiInfo
} from 'react-icons/fi';
import DashboardLayout from '../components/DashboardLayout';
import { useAuth } from '../services/auth';
import { getCurrentUserProfile, updateUserProfile } from '../services/userService';
import '../styles/Settings.scss';

const Settings = () => {
  const navigate = useNavigate();
  const { signOut } = useAuth();
  const [profile, setProfile] = useState(null);
  const [notificationsEnabled, setNotificationsEnabled] = useState(true);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadProfile();
  }, []);

  const loadProfile = async () => {
    try {
      const data = await getCurrentUserProfile();
      setProfile(data);
      setNotificationsEnabled(data?.notifications_enabled !== false);
    } catch (error) {
      console.error('Error loading profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleNotifications = async () => {
    try {
      const newValue = !notificationsEnabled;
      setNotificationsEnabled(newValue);
      
      await updateUserProfile({
        notifications_enabled: newValue,
      });
    } catch (error) {
      console.error('Error updating notifications:', error);
      setNotificationsEnabled(!notificationsEnabled);
      alert('Failed to update notification settings');
    }
  };

  const handleSignOut = () => {
    if (window.confirm('Are you sure you want to sign out?')) {
      signOut();
      navigate('/login');
    }
  };

  const SettingsItem = ({ icon: Icon, title, subtitle, to, onClick, color = '#7C4DFF', rightElement }) => (
    <motion.div 
      className="settings-item"
      whileHover={{ x: 4 }}
      onClick={onClick}
    >
      {to ? (
        <Link to={to} className="settings-item-link">
          <div className="settings-item-content">
            <div className="settings-icon" style={{ background: `${color}20` }}>
              <Icon style={{ color }} />
            </div>
            <div className="settings-text">
              <h4>{title}</h4>
              {subtitle && <p>{subtitle}</p>}
            </div>
          </div>
          {rightElement || <FiChevronRight className="chevron" />}
        </Link>
      ) : (
        <div className="settings-item-link">
          <div className="settings-item-content">
            <div className="settings-icon" style={{ background: `${color}20` }}>
              <Icon style={{ color }} />
            </div>
            <div className="settings-text">
              <h4>{title}</h4>
              {subtitle && <p>{subtitle}</p>}
            </div>
          </div>
          {rightElement || <FiChevronRight className="chevron" />}
        </div>
      )}
    </motion.div>
  );

  if (loading) {
    return (
      <DashboardLayout>
        <div className="loading-container">
          <div className="spinner"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="settings-page">
        <div className="settings-container">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <h1 className="settings-title">Settings</h1>
          </motion.div>

          {/* Account Section */}
          <motion.div 
            className="settings-section glass-card"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <h3 className="section-title">Account</h3>
            
            <SettingsItem
              icon={FiUser}
              title="Account Details"
              subtitle="Email, phone, password"
              to="/account-details"
              color="#7C4DFF"
            />
          </motion.div>

          {/* Notifications Section */}
          <motion.div 
            className="settings-section glass-card"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <h3 className="section-title">Notifications</h3>
            
            <SettingsItem
              icon={FiBell}
              title="Push Notifications"
              subtitle={notificationsEnabled ? 'Enabled' : 'Disabled'}
              color="#4CD964"
              rightElement={
                <label className="toggle-switch">
                  <input
                    type="checkbox"
                    checked={notificationsEnabled}
                    onChange={handleToggleNotifications}
                  />
                  <span className="toggle-slider"></span>
                </label>
              }
            />
          </motion.div>

          {/* Privacy & Safety Section */}
          <motion.div 
            className="settings-section glass-card"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            <h3 className="section-title">Privacy & Safety</h3>
            
            <SettingsItem
              icon={FiShield}
              title="Blocked Users"
              subtitle="Manage blocked accounts"
              to="/blocked-users"
              color="#EF4444"
            />
          </motion.div>

          {/* About Section */}
          <motion.div 
            className="settings-section glass-card"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
          >
            <h3 className="section-title">About</h3>
            
            <SettingsItem
              icon={FiInfo}
              title="App Version"
              subtitle="1.0.0"
              color="#3B82F6"
            />

            <SettingsItem
              icon={FiLock}
              title="Terms of Service"
              to="/terms-and-conditions"
              color="#3B82F6"
            />

            <SettingsItem
              icon={FiShield}
              title="Privacy Policy"
              to="/privacy-policy"
              color="#3B82F6"
            />
          </motion.div>

          {/* Sign Out */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
          >
            <button 
              className="sign-out-button"
              onClick={handleSignOut}
            >
              <FiLogOut />
              <span>Sign Out</span>
            </button>
          </motion.div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default Settings;